/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

// cspell:ignore cacheable formenctype multipart signup

/**
 * HTTP defines a set of request methods to indicate the desired action to be performed for a given resource. Although
 * they can also be nouns, these request methods are sometimes referred as _HTTP_ verbs. Each of them implements a
 * different semantic, but some common features are shared by a group of them: For example, a request method can be
 * safe, idempotent, or cacheable.
 */
export type HttpMethod =
  | HttpMethodDelete
  | HttpMethodGet
  | HttpMethodHead
  | HttpMethodOptions
  | HttpMethodPatch
  | HttpMethodPost
  | HttpMethodPut;

/**
 * The type of a `DELETE` request.
 */
export type HttpMethodDelete = typeof HTTP_METHOD_DELETE;

/**
 * The type of a `GET` request.
 */
export type HttpMethodGet = typeof HTTP_METHOD_GET;

/**
 * The type of a `HEAD` request.
 */
export type HttpMethodHead = typeof HTTP_METHOD_HEAD;

/**
 * The type of a `OPTIONS` request.
 */
export type HttpMethodOptions = typeof HTTP_METHOD_OPTIONS;

/**
 * The type of a `PATCH` request.
 */
export type HttpMethodPatch = typeof HTTP_METHOD_PATCH;

/**
 * The type of a `POST` request.
 */
export type HttpMethodPost = typeof HTTP_METHOD_POST;

/**
 * The type of a `PUT` request.
 */
export type HttpMethodPut = typeof HTTP_METHOD_PUT;

/**
 * The `DELETE` method deletes the specified resource.
 */
export const HTTP_METHOD_DELETE = `DELETE`;

/**
 * The `GET` method requests a representation of the specified resource. Requests using `GET` should only retrieve data.
 */
export const HTTP_METHOD_GET = `GET`;

/**
 * The `HEAD` method requests the headers that are returned if the specified resource would be requested with a `GET`
 * method. Such a request can be done before deciding to download a large resource to save bandwidth, for example.
 *
 * A response to a `HEAD` method should not have a body. If so, it must be ignored. Even so, entity headers describing
 * the content of the body, like `Content-Length` may be included in the response. They don't relate to the body of the
 * `HEAD` response, which should be empty, but to the body of similar request using the `GET` method would have returned
 * as a response.
 *
 * If the result of a `HEAD` request shows that a cached resource after a `GET` request is now outdated, the cache is
 * invalidated, even if no `GET` request has been made.
 */
export const HTTP_METHOD_HEAD = `HEAD`;

/**
 * The `OPTIONS` method is used to describe the communication options for the target resource. The client can specify a
 * `URL` for the `OPTIONS` method, or an asterisk (*) to refer to the entire server.
 */
export const HTTP_METHOD_OPTIONS = `OPTIONS`;

/**
 * The PATCH request method applies partial modifications to a resource.
 *
 * The `PUT` method only allows complete replacement of a document. Unlike `PUT`, `PATCH` is not idempotent, meaning
 * successive identical patch requests may have different effects. However, it is possible to issue `PATCH` requests in
 * such a way as to be idempotent.
 *
 * `PATCH` (like `PUT`) _may_ have side-effects on other resources.
 *
 * To find out whether a server supports `PATCH`, a server can advertise its support by adding it to the list in the
 * `Allow` or `Access-Control-Allow-Methods` (for CORS) response headers.
 *
 * Another (implicit) indication that `PATCH` is allowed, is the presence of the `Accept-Patch` header, which specifies
 * the patch document formats accepted by the server.
 */
export const HTTP_METHOD_PATCH = `PATCH`;

/**
 * The `POST` method sends data to the server. The type of the body of the request is indicated by the `Content-Type`
 * header.
 *
 * The difference between `PUT` and `POST` is that `PUT` is idempotent: calling it once or several times successively
 * has the same effect (that is no _side_ effect), where successive identical `POST` may have additional effects,
 * like passing an order several times.
 *
 * A `POST` request is typically sent via an HTML form and results in a change on the server. In this case, the content
 * type is selected by putting the adequate string in the `enctype` attribute of the `<form>` element or the
 * `formenctype` attribute of the `<input>` or `<button>` elements:
 *
 * * `application/x-www-form-urlencoded`: The keys and values are encoded in key-value tuples separated by `'&'`, with a
 * `'='` between the key and the value. Non-alphanumeric characters in both keys and values are percent encoded: this is
 * the reason why this type is not suitable to use with binary data (use `multipart/form-data` instead)
 * * `multipart/form-data`: Each value is sent as a block of data ("body part"), with a user agent-defined delimiter
 * ("boundary") separating each part. The keys are given in the `Content-Disposition` header of each part.
 * * `text/plain`: Plain text.
 *
 * When the `POST` request is sent via a method other than an HTML form — like via an `XMLHttpRequest` — the body can
 * take any type. As described in the HTTP 1.1 specification, `POST` is designed to allow a uniform method to cover the
 * following functions:
 *
 * * Annotation of existing resources
 * * Posting a message to a bulletin board, newsgroup, mailing list, or similar group of articles;
 * * Adding a new user through a signup modal;
 * * Providing a block of data, such as the result of submitting a form, to a data-handling process;
 * * Extending a database through an append operation.
 */
export const HTTP_METHOD_POST = `POST`;

/**
 * The `PUT` request method creates a new resource or replaces a representation of the target resource with the request
 * payload.
 *
 * The difference between `PUT` and `POST` is that `PUT` is idempotent: calling it once or several times successively
 * has the same effect (that is no _side_ effect), where successive identical `POST` may have additional effects, like
 * passing an order several times.
 */
export const HTTP_METHOD_PUT = `PUT`;
